import {
  faCircleMinus,
  faCirclePlus,
  faExclamationCircle,
  faLock,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, Tooltip } from '@mui/material';
import { Skeleton } from '@mui/material';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import Input from '../../../../components/input/input';
import {
  extractToken,
  fixTokenName,
  IMMERSIVE_LIVE_ID,
  QuickPricerContext,
  Surface,
} from '../../../../providers/otc-trader/quick-pricer-provider';
import Utils from '../../../../utils/immersive_utils';
import { GROUP_TRADES, Trade, TradeParamsEnum, TRADES_PARAMS_LABELS } from '../../trade-models';
import FilteredList from '../filterable-list';

import SolveButton from './solve-button';

import './styles.scss';

interface ITradeProps {
  trade: Trade;
  updateTrade: (id: string, trade: Trade) => void;
  deleteTrade: (id: string) => void;
  duplicateTrade: (id: string) => void;
  calculateTrade: (id: string) => void;
  getTradeData: (trade: Trade) => void;
  isLoading?: boolean | false;
}

const LIVE_DATA_SURFACE_ITEM = {
  id: IMMERSIVE_LIVE_ID,
  organisation: '',
  source: '',
  symbol: '',
  tag: 'IMMERSIVE - LIVE',
  timestamp: '',
  user: '',
  isLiveData: true,
};

const LATEST_SURFACE_ITEM: Surface = {
  id: 'latest',
  organisation: '',
  source: '',
  symbol: '',
  tag: 'Latest',
  timestamp: '',
  user: '',
  isLiveData: true,
};

const HAS_LIVE_TOKENS = ['XRP', 'BTC', 'ETH', 'SOL', 'BNB'];

const checkIfHasLiveToken = (token: string) => {
  const symbol = token.split('_')[0] || token;

  return HAS_LIVE_TOKENS.includes(symbol);
};

const TradeItem = (props: ITradeProps) => {
  const { trade, updateTrade, deleteTrade, duplicateTrade, isLoading, calculateTrade } = props;

  const {
    tkPair,
    expandableColumns,
    surfaces: surfaceList,
    getSurfaces,
    filterSurfacesPerToken,
    getExistentSurfacePerToken,
    getTradeData,
  } = useContext(QuickPricerContext);

  const [surfaces, setSurfaces] = useState<Surface[]>([LATEST_SURFACE_ITEM]);
  const [selectedSurface, setSelectedSurface] = useState<Surface | undefined>(LATEST_SURFACE_ITEM);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleCalculateTrade = (tradeParam?: Trade, solveFor?: TradeParamsEnum) => {
    if (tradeParam) {
      getTradeData(tradeParam, solveFor);
    } else {
      calculateTrade(trade.id);
    }
  };

  const handleDuplicateTrade = () => {
    duplicateTrade(trade.id);
  };

  const handleDeleteTrade = () => {
    deleteTrade(trade.id);
  };

  const setInputCursor = (
    start: number,
    end: number,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setTimeout(() => {
      event.target.setSelectionRange(start, end);
    }, 0);
  };

  const getInputSelection = (target: HTMLInputElement) => {
    const start = target.selectionStart || 0;
    const end = target.selectionEnd || target.value.length - 1;

    return [start, end];
  };

  const wrapperUpdateTrade = (
    id: string,
    trade: Trade,
    event?: React.ChangeEvent<HTMLInputElement>,
  ) => {
    updateTrade(id, trade);

    if (event) {
      const [start, end] = getInputSelection(event.target);

      setInputCursor(start, end, event);
    }
  };

  const handleChangeValue = (e: React.ChangeEvent<HTMLInputElement>, name: TradeParamsEnum) => {
    const inputValue = `${e.target.value}`.toUpperCase();

    let startWithToken = false;

    if (name === TradeParamsEnum.instrument && !!tkPair && trade.token) {
      startWithToken = !(inputValue.split('_')[0] === trade.token.split('_')[0]);
    }

    const newTrade = {
      ...trade,
      [name]: startWithToken ? trade.token?.split('_')[0] : inputValue,
    };

    wrapperUpdateTrade(trade.id, newTrade, e);
  };

  const checkIfCanOverride = (key: TradeParamsEnum) => {
    const item = TRADES_PARAMS_LABELS.find((tp) => tp.key === key);

    if (!item) return false;

    return !!item.override;
  };
  const checkIfCanSolve = (key: TradeParamsEnum) => {
    const item = TRADES_PARAMS_LABELS.find((tp) => tp.key === key);

    if (!item) return false;

    return !!item.canSolve;
  };

  const compareAndUpdateToken = (token: string, savedToken: string) => {
    const [tokenPrefix, tokenSuffix] = token.split('_');
    const [savedTokenPrefix, savedTokenSuffix] = savedToken.split('_');

    if (!savedToken) {
      if (!tokenSuffix) {
        const existentSurface = getExistentSurfacePerToken(tokenPrefix);

        if (existentSurface) {
          return existentSurface;
        }

        return fixTokenName(token);
      }

      return token;
    }

    if (token === savedToken) return savedToken;

    if (tokenPrefix !== savedTokenPrefix) {
      const existentSurface = getExistentSurfacePerToken(tokenPrefix);

      if (existentSurface) {
        return existentSurface;
      }

      return fixTokenName(token);
    }

    if (!tokenSuffix) {
      if (!savedTokenSuffix) {
        return savedToken;
      }

      return savedToken;
    }

    if (savedTokenSuffix !== tokenSuffix) return token;

    return token;
  };

  const handleToken = (inputValue: string, savedToken?: string) => {
    const token = extractToken(inputValue);
    const nToken = compareAndUpdateToken(token, savedToken || '');

    if (nToken !== savedToken) {
      const newTrade = {
        ...trade,
        token: nToken,
        surface: LATEST_SURFACE_ITEM.id,
      };

      wrapperUpdateTrade(trade.id, newTrade);
      handleCalculateTrade(newTrade);
      return true;
    }

    return false;
  };

  const onKeyDown = (e: any, key: TradeParamsEnum) => {
    if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();

      if (key === TradeParamsEnum.instrument) {
        const inputValue = e.target.value.toUpperCase();

        const wasUpdatedToken = handleToken(inputValue, trade.token);

        if (wasUpdatedToken) return;
      }

      if (checkIfCanOverride(key)) {
        const currentOverrideItems = trade.overrideItems || {};

        let solveItem = trade.solveItem;

        if (checkIfCanSolve(key)) {
          solveItem = solveItem === key ? undefined : key;
        }

        const newTrade: Trade = {
          ...trade,
          overrideItems: {
            ...currentOverrideItems,
            [key]: trade[key],
          },
          solveItem,
        };

        wrapperUpdateTrade(trade.id, newTrade);
        handleCalculateTrade(newTrade, checkIfCanSolve(key) ? TradeParamsEnum.vol : undefined);
        return;
      }

      handleCalculateTrade();
    }
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement, Element>, key: TradeParamsEnum) => {
    if (key === TradeParamsEnum.instrument) {
      const inputValue = e.target.value.toUpperCase();

      const wasUpdatedToken = handleToken(inputValue, trade.token);

      if (wasUpdatedToken) return;
    }
  };

  const clearDataFixed = (key: TradeParamsEnum) => {
    const currentOverrideItems = trade.overrideItems || {};
    delete currentOverrideItems[key];

    const newTrade: Trade = {
      ...trade,
      overrideItems: {
        ...currentOverrideItems,
      },
      solveItem: undefined,
    };

    handleCalculateTrade(newTrade);
  };

  const handleOnSelectChange = useCallback(
    (value: any) => {
      const newTrade = {
        ...trade,
        [TradeParamsEnum.surface]: value.id.toString(),
      };

      wrapperUpdateTrade(trade.id, newTrade);
      getTradeData(newTrade);
    },
    [trade, getTradeData, wrapperUpdateTrade],
  );

  const setDefaultSurfaces = () => {
    let defaultSurfaces = [LATEST_SURFACE_ITEM];

    if (trade.token && checkIfHasLiveToken(trade.token)) {
      defaultSurfaces = [LIVE_DATA_SURFACE_ITEM, ...defaultSurfaces];
    }

    setSurfaces(defaultSurfaces);
  };

  useEffect(() => {
    const surface = surfaces.find((sf) => sf.id.toString() === trade.surface);

    if (surface) {
      setSelectedSurface(surface);
    }
  }, [setSelectedSurface, trade.surface, surfaces]);

  const renderSelect = useMemo(() => {
    return (
      <FilteredList
        key={TradeParamsEnum.surface}
        values={surfaces}
        defaultValue={selectedSurface}
        onChange={(value) => {
          handleOnSelectChange(value);
        }}
        isQuickPricer
        className="filtered-list"
      />
    );
  }, [surfaces, selectedSurface, handleOnSelectChange]);

  useEffect(() => {
    if (trade.token && surfaceList) {
      let list = filterSurfacesPerToken(trade.token);
      list = [LATEST_SURFACE_ITEM, ...list];

      if (list && list.length) {
        if (trade.token && checkIfHasLiveToken(trade.token)) {
          list = [LIVE_DATA_SURFACE_ITEM, ...list];
        }

        setSurfaces(list);
        return;
      }
    }

    setDefaultSurfaces();
  }, [surfaceList, trade.token]);

  useEffect(() => {
    if (trade.token) {
      getSurfaces(trade.token);
    }
  }, [trade.token]);

  const renderTradeFields = () => {
    const getParent = (key: TradeParamsEnum) => {
      return GROUP_TRADES.find((group) => group.children.includes(key));
    };

    return TRADES_PARAMS_LABELS.map((item) => {
      const parent = getParent(item.key);
      const isChild = !!parent;

      const canRenderChild = isChild && expandableColumns.includes(parent.key);

      const canRender = !isChild || canRenderChild;

      if (!canRender) return null;

      const canOverride = checkIfCanOverride(item.key);
      const overrideData = trade?.overrideItems?.[item.key];
      const hasOverrideData = canOverride && overrideData;
      const isSolved = item.key === trade.solve_for && trade.isSolved;

      if (item.key === TradeParamsEnum.surface) {
        return renderSelect;
      }

      if (item.type === 'input' && !hasOverrideData) {
        let className = 'trade-input';

        if (trade.error && trade.error?.key === item.key) {
          className += ' trade-input-error';
        }

        if (item.key !== TradeParamsEnum.instrument) {
          className += ' numeric-input';
        }

        if (isSolved) {
          className += ' solved-input';
        }

        return (
          <div className="trade-input-container" key={item.key}>
            <Input
              placeholder={item.label}
              value={trade[item.key]}
              onChange={(e) => handleChangeValue(e, item.key)}
              disabled={isLoading}
              className={className}
              onKeyDown={(e) => onKeyDown(e, item.key)}
              onBlur={(e) => onBlur(e, item.key)}
              ref={inputRef}
            />
          </div>
        );
      }

      if (isLoading) {
        return (
          <div className="trade-input-container" key={item.key}>
            <Skeleton
              key={item.key}
              className="trade-value"
              variant="rectangular"
              animation="wave"
            />
          </div>
        );
      }

      const value = hasOverrideData ? overrideData : trade[item.key];
      let valueClassName = `trade-value ${item.align === 'right' ? 'align-right' : ''}`;

      if (hasOverrideData) {
        valueClassName += ' override-value';
      }

      return (
        <div className="trade-input-container" key={item.key}>
          <div className={valueClassName}>
            <span {...Utils.obscuredProps}>{value || item.placeholder}</span>

            {hasOverrideData && (
              <IconButton onClick={() => clearDataFixed(item.key)} className="spot-fixed">
                <FontAwesomeIcon icon={faLock} className="icon" />
              </IconButton>
            )}
          </div>
        </div>
      );
    });
  };

  const renderError = () => {
    return (
      <div className="trade-item-error">
        <FontAwesomeIcon icon={faExclamationCircle} className="icon" />
        <span>{trade.error?.error}</span>
      </div>
    );
  };

  const handleSolve = (param: TradeParamsEnum) => {
    getTradeData(trade, param);
  };

  const hasSolveItems = !!trade.solveItem;

  return (
    <div className="trade-item-container">
      <div className="trade-item-row">
        <div className="trade-item-fields">{renderTradeFields()}</div>
        <div className="actions-buttons">
          <SolveButton hasSolveItems={hasSolveItems} onSolve={handleSolve} />
          <Tooltip title="Duplicate">
            <IconButton className="action-button" onClick={handleDuplicateTrade}>
              <FontAwesomeIcon icon={faCirclePlus} className="icon" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove">
            <IconButton className="action-button" onClick={handleDeleteTrade}>
              <FontAwesomeIcon icon={faCircleMinus} className="icon" />
            </IconButton>
          </Tooltip>
        </div>
      </div>
      {trade.error ? renderError() : <></>}
    </div>
  );
};

export default TradeItem;
